/**
 * Cart Initialization File
 * 
 * This file connects all the cart components and initializes the cart system.
 * It should be included in the theme after CartHelpers.js, CartStore.js, CartService.js,
 * cart.js, and cart-drawer.js are loaded.
 */

import { cartStore, ACTIONS } from './CartStore.js';
import { cartService } from './CartService.js';
import { updateLiveRegion } from './CartHelpers.js';

// Debug mode flag - set to true to enable verbose logging
const CART_DEBUG = false;

/**
 * Log helper that only logs in debug mode
 */
function debugLog(...args) {
  if (CART_DEBUG) {
    console.log('[CartDebug]', ...args);
  }
}

/**
 * Initialize the cart system
 */
function initCart() {
  debugLog('Initializing cart system');
  
  // Subscribe to cart state changes for debugging
  if (CART_DEBUG) {
    cartStore.subscribe((state) => {
      debugLog('Cart state updated: ', state);
    });
  }
  
  // Subscribe to cart state changes for header cart icon updates
  cartStore.subscribe((state) => {
    updateCartIconBubble(state);
  });
  
  // Initial cart load
  cartService.getCart().then(cartData => {
    debugLog('Initial cart loaded:', cartData);
  }).catch(error => {
    console.error('Error loading initial cart:', error);
  });
  
  // Add event listener for product forms to intercept add-to-cart
  setupAddToCartInterception();
}

/**
 * Update the cart icon in the header with the current cart count
 */
function updateCartIconBubble(state) {
  try {
    const cartIconBubble = document.querySelector('#cart-notifier .cart-quantity');
    if (cartIconBubble) {
      cartIconBubble.textContent = state.itemCount;
      
      // Toggle visibility based on cart count
      if (state.itemCount === 0) {
        cartIconBubble.classList.add('hidden');
      } else {
        cartIconBubble.classList.remove('hidden');
      }
    }
  } catch (error) {
    console.error('Error updating cart icon:', error);
  }
}

/**
 * Set up event listeners for add-to-cart forms
 */
function setupAddToCartInterception() {
  document.addEventListener('submit', async function(event) {
    const form = event.target;
    
    // Only intercept product forms
    if (form.action && form.action.indexOf('/cart/add') !== -1) {
      event.preventDefault();
      
      debugLog('Intercepted add to cart form submission', form);
      
      try {
        // Show loading state
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.classList.add('loading');
          submitButton.disabled = true;
        }
        
        // Get form data
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = formData.get('quantity') || 1;
        
        // Extract properties if any
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            const propName = key.slice(11, -1); // Extract name from properties[name]
            properties[propName] = value;
          }
        }
        
        // Add item to cart via CartService
        await cartService.addItem(variantId, parseInt(quantity), Object.keys(properties).length ? properties : undefined);
        
        // Open cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.open();
        }
        
        // Show success message
        updateLiveRegion(window.cartStrings?.added_to_cart || 'Item added to cart');
      } catch (error) {
        console.error('Error adding item to cart:', error);
        updateLiveRegion(error.message || window.cartStrings?.error || 'Error adding item to cart');
      } finally {
        // Reset button state
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.disabled = false;
        }
      }
    }
  });
}

// Initialize cart when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCart);
} else {
  initCart();
}

// Export cart modules for potential external usage
export { cartStore, cartService, initCart }; 